import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import {
  ScissorOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { c5m4Api, WriteoffItem } from '../../services/c5m4Api';
import { useC5M4Writeoff } from './hooks/useC5M4Writeoff';
import DetailDrawer from './components/DetailDrawer';
import './C5M4Writeoff.css';
import { BatchAcceptanceBridge, pwc5m4f1AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;

// ============================================================
// Helpers
// ============================================================

function renderApprovalStat(stat: string): React.ReactNode {
  if (stat === 'D') return <Tag color="orange">送審中</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  if (stat === 'N') return <Tag color="red">已駁回</Tag>;
  if (stat === '') return <Tag color="default">草稿</Tag>;
  return <Tag color="default">{stat || '—'}</Tag>;
}

function formatNumber(value: number, decimals = 2): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('zh-TW');
}

// ============================================================
// Main Component
// ============================================================

const C5M4Writeoff: React.FC = () => {
  const {
    loading,
    writeoffing,
    candidateItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    setFilter,
    setSelectedKeys,
    fetchCandidates,
    batchWriteoff,
    loadDetail,
    closeDetail,
    lastApiResponse,
  } = useC5M4Writeoff();

  React.useEffect(() => {
    fetchCandidates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<WriteoffItem> = [
    {
      title: <span data-contract-field="fc120_01">交運單號</span>,
      dataIndex: 'do_no',
      key: 'do_no',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    { title: <span data-contract-field="fc120_10">S/C No</span>, dataIndex: 'sc_no', key: 'sc_no', width: 100 },
    {
      title: <span data-contract-field="fc120_11">交運日期</span>,
      dataIndex: 'do_date',
      key: 'do_date',
      width: 110,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: <span data-contract-field="ui_customer_name">客戶名稱</span>,
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="ui_dept_name">部門</span>,
      dataIndex: 'dept_name',
      key: 'dept_name',
      width: 100,
      ellipsis: true,
    },
    { title: <span data-contract-field="fc120_18">幣別</span>, dataIndex: 'currency', key: 'currency', width: 60, align: 'center' },
    {
      title: <span data-contract-field="fc120_20">未稅金額</span>,
      dataIndex: 'untaxed_amount',
      key: 'untaxed_amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatNumber(v),
    },
    {
      title: <span data-contract-field="fc120_201">含稅總額</span>,
      dataIndex: 'taxed_amount',
      key: 'taxed_amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatNumber(v),
    },
    {
      title: <span data-contract-field="fc120_80">狀態</span>,
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 90,
      align: 'center',
      render: renderApprovalStat,
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: WriteoffItem) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => loadDetail(record.do_no)}
        >
          明細
        </Button>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchCandidates(); };
  const handleFilterChange = (field: 'doStart' | 'doEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  return (
    <div className="c5m4-container">
      <Title level={4} style={{ marginBottom: 16 }}>交運單沖銷 (pwc5m4f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="query_do_start">交運單號 起始</span>}>
            <Input
              value={filter.doStart}
              onChange={(e) => handleFilterChange('doStart', e.target.value)}
              placeholder="例：D24001"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="query_do_end">交運單號 結束</span>}>
            <Input
              value={filter.doEnd}
              onChange={(e) => handleFilterChange('doEnd', e.target.value)}
              placeholder="例：D24099"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} data-acceptance-action="PWC5M4-ACT-QUERY">
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">共 <strong>{total}</strong> 筆可沖銷</Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">已勾選 <strong>{selectedKeys.length}</strong> 筆</Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆交運單？沖銷後狀態將回復為草稿。`}
              onConfirm={batchWriteoff}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
            >
              <Button
                danger
                icon={<ScissorOutlined />}
                loading={writeoffing}
                disabled={selectedKeys.length === 0}
                data-acceptance-action="PWC5M4-ACT-WRITEOFF"
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<WriteoffItem>
        rowKey="do_no"
        columns={columns}
        dataSource={candidateItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ x: 1000 }}
        locale={{ emptyText: '無可沖銷資料' }}
      />

      <DetailDrawer visible={detailVisible} loading={detailLoading} data={detailData} onClose={closeDetail} />
      <BatchAcceptanceBridge
        manifest={pwc5m4f1AcceptanceManifest}
        records={candidateItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.do_no}
        queryExact={(key) => c5m4Api.getDetail(key)}
        lastApiResponse={lastApiResponse}
        loading={loading || writeoffing || detailLoading}
        onAutoFill={() => {
          const first = candidateItems[0];
          if (first) setSelectedKeys([first.do_no]);
        }}
        autoFillDisabled={candidateItems.length === 0}
        autoFillReason="安全勾選第一張可沖銷交運單，不會自動執行沖銷。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default C5M4Writeoff;
